import {
  ConnectedPosition,
  Overlay,
  OverlayConfig,
  OverlayRef,
  PositionStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ChipModule } from '~ui-kit/chip';
import { isArray, isEmpty, without } from 'lodash-es';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { normalizeString } from '~ui-kit/core/utils';

@Component({
  selector: 'tags-selector',
  standalone: true,
  host: {
    role: 'combobox',
    'aria-autocomplete': 'none',
    'aria-haspopup': 'listbox',
    '[attr._id]': '_id() || null',
    '[attr.aria-expanded]': '_panelOpen()',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-required]': '_isRequired().toString()',
    '[attr.aria-disabled]': '_isDisabled().toString()',
    '[attr.aria-readonly]': 'readonly().toString()',
    '[attr.aria-invalid]': '_isInvalid().toString()',
    class: 'tags-selector',
    '[class.tags-selector--expanded]': '_panelOpen()',
    '[class.input-disabled]': '_isDisabled()',
    '[class.input-readonly]': 'readonly()',
    '[class.tags-selector--multilines]': 'multilines()'
  },
  template: `<div class="tags-selector__wrapper">
    <div class="tags-selector-trigger custom-scrollbar">
      @for (tag of _selectedTags(); track $index) {
        <span
          chip
          size="sm"
          appearance="filled-tonal"
          [dismissible]="true"
          color="primary"
          class="tags-selector-trigger__added-tag"
          (dismissed)="removeTag(tag)"
        >
          {{ tag }}
        </span>
      }
      <span
        contentEditable="true"
        class="tags-selector-trigger__input"
        [attr.data-placeholder]="placeholder()"
        [class.show-placeholder]="_canShowPlaceholder()"
        (keydown.Enter)="$event.preventDefault()"
        #inputElement
      ></span>
    </div>
    <ng-template #panelTpl>
      <div
        class="tags-selector-panel"
        [class]="additionalClass() || ''"
        [style.min-width.px]="hostFormField ? hostFormField.clientWidth : 0"
      >
        <ul class="tag-list">
          @for (tag of _filteredTags(); track $index) {
            @if ({ isSelected: _selectedTags().includes(tag) }; as tagOption) {
              <li
                class="tag-option"
                role="button"
                [class.tag-option--selected]="tagOption.isSelected"
                [attr.aria-selected]="tagOption.isSelected.toString()"
                (click)="toggleTag(tag)"
              >
                <span class="tag-option__check-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="8"
                    viewBox="0 0 10.813 7.958"
                  >
                    <path
                      d="M-9727.816,4720.984l2.441,2.441,5.544-5.544"
                      transform="translate(9729.23 -4716.467)"
                      fill="none"
                      stroke="#199e46"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    />
                  </svg>
                </span>
                {{ tag }}
              </li>
            }
          }

          @if (_filteredTagsIsEmpty()) {
            <li class="tag-option-no-found">
              {{ noResultFeedback() }},
              <button
                type="button"
                class="tag-option-no-found__add"
                (click)="onAddBtnClick()"
              >
                {{ addLabel() }}
              </button>
            </li>
          }
        </ul>
      </div>
    </ng-template>
  </div>`,
  imports: [CommonModule, ReactiveFormsModule, ChipModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TagsSelectorComponent)
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagsSelectorComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Output() readonly valueChange: EventEmitter<Array<string>> =
    new EventEmitter<Array<string>>();

  @Output() readonly openedChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @Output() readonly opened: Observable<void> = this.openedChange.pipe(
    filter(o => o),
    map(() => {})
  );

  @Output() readonly closed: Observable<void> = this.openedChange.pipe(
    filter(o => !o),
    map(() => {})
  );

  placeholder = input<string | undefined | null>();
  uniqueId = input.required<string>();
  ariaLabel = input('');
  disabled = input(false);
  readonly = input(false);
  additionalClass = input<string[] | undefined>();
  panelClass = input<string | string[]>('');
  noResultFeedback = input<string | undefined | null>();
  addLabel = input<string | undefined | null>();
  tags = input<string[]>([]);
  selectedTags = input<string[]>([]);
  multilines = input(false);

  panelTemplate = viewChild<TemplateRef<unknown>>('panelTpl');

  inputElement = viewChild<ElementRef<HTMLElement>>('inputElement');

  protected _panelOpen = signal(false);
  protected _selectedTags = signal<string[]>(this.selectedTags());
  protected _id = signal('');
  protected _isInvalid = signal(false);
  protected _isDisabled = signal(false);
  protected _isRequired = signal(false);
  protected _filteredTags: WritableSignal<string[]> = signal([]);
  protected _canShowPlaceholder = signal(true);

  protected _filteredTagsIsEmpty = computed(() =>
    isEmpty(this._filteredTags())
  );

  onChange: (value: unknown) => void = () => {};

  onTouched: () => void = () => {};

  private _panelPortal: TemplatePortal<unknown> | undefined;

  private _overlayRef: OverlayRef | undefined;

  private get _host(): HTMLElement {
    return this._vcr.element.nativeElement as HTMLElement;
  }

  get hostFormField(): HTMLElement | null {
    return this._host.closest('.form-field') as HTMLElement | null;
  }

  get editableContent(): HTMLElement {
    return this._host.querySelector(
      '.tags-selector-trigger__input'
    ) as HTMLElement;
  }

  get canOpenPanel(): boolean {
    return (
      !!this.tags().length &&
      (!!this._filteredTags().length ||
        (!this._filteredTags().length &&
          !!this.noResultFeedback() &&
          !!this.addLabel()))
    );
  }

  private _positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      panelClass: 'select-panel-above'
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      panelClass: 'select-panel-above'
    }
  ];

  private get _ngControl(): NgControl | null {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });

    return ngControl;
  }

  constructor(
    private _overlay: Overlay,
    private _destroyRef: DestroyRef,
    private _vcr: ViewContainerRef,
    private _injector: Injector
  ) {}

  ngOnInit(): void {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
      this._isRequired.set(
        this._ngControl.control?.hasValidator(Validators.required) ?? false
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      this._isDisabled.set(this.disabled());
    }

    if ('readonly' in changes) {
      this._setFormFieldCursorStyle();
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this._id.set(this.uniqueId());
      }
    }

    if ('tags' in changes) {
      this._filteredTags.set(this.tags());
    }

    if ('selectedTags' in changes) {
      this._selectedTags.set(this.selectedTags());
    }
  }

  ngAfterViewInit(): void {
    if (!this.hostFormField) {
      throw new Error(
        'To ensure proper functionality, SelectComponent should be hosted within a FormFieldComponent.'
      );
    }

    this._panelPortal = new TemplatePortal(this.panelTemplate()!, this._vcr);
    this._handleInputElementClick();
    this._handleInputElementKeyUp();
    this._handleInputElementContentChanges();
    this._handleInputElementFocus();
    this._handleInputElementBlur();
    this._setFormFieldCursorStyle();
    this._updateCanShowPlaceholderValue();
  }

  ngOnDestroy(): void {
    this._closePanel();
  }

  click(): void {
    this.editableContent.focus();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  writeValue(value: unknown): void {
    if (
      typeof value === 'object' &&
      isArray(value) &&
      value.some(item => typeof item === 'string')
    ) {
      value.forEach(tag => {
        if (tag.trim()) {
          this._selectedTags.set([...this._selectedTags(), tag]);
        }
      });
      this._handleTagListChanges(this._selectedTags());
    }
  }

  setDisabledState(_isDisabled: boolean): void {
    this._isDisabled.set(_isDisabled);
  }

  toggleTag(tag: string): void {
    if (this._selectedTags().includes(tag)) {
      this.removeTag(tag);
    } else {
      this._selectedTags.set([...this._selectedTags(), tag]);
    }
    this.click();
    this._handleTagListChanges(this._selectedTags());
    const tagsWrapper = this.editableContent.parentElement!;
    setTimeout(() => {
      tagsWrapper.scrollLeft = tagsWrapper.scrollWidth;
    }, 100);
  }

  removeTag(tag: string): void {
    this._selectedTags.set(without(this._selectedTags(), tag));
    this._handleTagListChanges(this._selectedTags());
    this.editableContent.focus();
  }

  onAddBtnClick(): void {
    this.toggleTag((this.editableContent.textContent || '').trim());
    this._clearInput();
    this.editableContent.focus();
    this._closePanel();
  }

  private _onOpenPanel(): void {
    if (this._overlayRef || !this.hostFormField) return;
    const formFieldWrapper = this.hostFormField.querySelector(
      '.form-field__wrapper'
    ) as HTMLElement | null;

    if (!formFieldWrapper || !this.canOpenPanel) return;

    this._overlayRef = this._overlay.create(
      this._getOverlayConfig(formFieldWrapper)
    );
    this._overlayRef.addPanelClass(this.panelClass());
    this._overlayRef.attach(this._panelPortal);
    this._handleOverlayBackdropClick();
    this._panelOpen.set(true);
    this._emitOpenChange();
  }

  private _closePanel(): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
    this._overlayRef = undefined;
    this._panelOpen.set(false);
    this._clearInput();
    this._removeClassFromHostElement('input-focused');
    this._updateCanShowPlaceholderValue();
    this._emitOpenChange();
  }

  private _handleInputElementClick(): void {
    fromEvent(this.editableContent, 'click')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => {
          if (!this._filteredTagsIsEmpty()) {
            this._onOpenPanel();
            this._addClassToHostElement('input-focused');
          }
          return of(undefined);
        })
      )
      .subscribe();
  }

  private _handleInputElementKeyUp(): void {
    fromEvent(this.editableContent, 'keyup')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(event => {
          if ((event as KeyboardEvent).key === 'Enter') {
            event.preventDefault();
            const tagInput = event.target as HTMLElement;
            const newTag = (tagInput.textContent || '').trim();
            if (newTag) {
              this.toggleTag(newTag);
            }
            this._clearInput();
            this._closePanel();
            this._updateCanShowPlaceholderValue();
          }
          return of(undefined);
        })
      )
      .subscribe();
  }

  private _handleInputElementContentChanges(): void {
    fromEvent(this.editableContent, 'input')
      .pipe(
        tap(() => {
          this._updateCanShowPlaceholderValue();
        }),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
        tap(event => {
          const tagInput = event.target as HTMLElement;
          const newTag = (tagInput.textContent || '').trim();
          this._filterTags(newTag);
          this._onOpenPanel();
        })
      )
      .subscribe();
  }

  private _handleInputElementFocus(): void {
    fromEvent(this.editableContent, 'focus')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this._filterTags((this.editableContent.textContent || '').trim());
          this._addClassToHostElement('input-focused');
        })
      )
      .subscribe();
  }

  private _handleInputElementBlur(): void {
    fromEvent(this.editableContent, 'blur')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this.onTouched();
          this._clearInput();
          this._removeClassFromHostElement('input-focused');
          this._updateCanShowPlaceholderValue();
        })
      )
      .subscribe();
  }

  private _handleOverlayBackdropClick(): void {
    this._overlayRef
      ?.backdropClick()
      .pipe(
        take(1),
        tap({
          next: () => {
            this._closePanel();
          }
        })
      )
      .subscribe();
  }

  private _getOverlayConfig(
    origin: HTMLElement,
    width?: number
  ): OverlayConfig {
    return new OverlayConfig({
      width,
      hasBackdrop: true,
      backdropClass: 'tags-selector-panel-backdrop',
      positionStrategy: this._getOverlayPosition(origin),
      scrollStrategy: this._overlay.scrollStrategies.reposition({
        autoClose: true
      })
    });
  }

  private _getOverlayPosition(origin: HTMLElement): PositionStrategy {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this._positions)
      .withPush(false);

    return positionStrategy;
  }

  private _addClassToHostElement(value: string): void {
    this._host.classList.add(value);
  }

  private _removeClassFromHostElement(value: string): void {
    this._host.classList.remove(value);
  }

  private _emitSelection(): void {
    this.valueChange.emit(this._selectedTags());
  }

  private _emitOpenChange(): void {
    this.openedChange.emit(this._panelOpen());
  }

  private _setFormFieldCursorStyle(): void {
    if (!this.hostFormField) return;
    (
      this.hostFormField.querySelector('.form-field__wrapper') as HTMLElement
    ).style.cursor = this.readonly() ? 'default' : 'pointer';
  }

  private _handleTagListChanges(tagList: string[]): void {
    this._emitSelection();
    this.onChange(tagList);
    tagList.length
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');
  }

  private _filterTags(tag: string): void {
    const filteredTags = this.tags().filter(item => {
      const normalizedItem = normalizeString(item);
      const normalizedTag = normalizeString(tag);

      return normalizedItem.includes(normalizedTag);
    });

    this._filteredTags.set(filteredTags);
  }

  private _updateCanShowPlaceholderValue(): void {
    const value = this.editableContent.textContent?.length;
    this._canShowPlaceholder.set(!value);
  }

  private _clearInput(): void {
    this.editableContent.textContent = '';
  }
}
